import { expValEquals } from '@confluence/feature-experiments';
import { isSpaceOverview } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { usePageContentId, usePageSpaceKey, useContentType } from '@confluence/page-context';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

type VisibilityKeys<T> = keyof T;

export const ELEMENTS = {
	ROWS: {
		CUSTOM_CONTENT: 'customContent',
		VIEWS: 'views',
		LENGTH: 'length',
		STATUS: 'status',
		APPROVAL_ACTIVITY: 'approvalActivity',
		REDACTIONS: 'redactions',
		LABELS: 'labels',
		PERFORMANCE_MODE: 'performanceMode',
	},
	SECTIONS: {
		STATS: 'stats',
		PROPERTIES: 'properties',
		CONNECTIONS: 'connections',
	},
};

export const SPACE_OVERVIEW_ELEMENT_VISIBILITY: {
	[ELEMENTS.ROWS.CUSTOM_CONTENT]: boolean;
	[ELEMENTS.ROWS.STATUS]: boolean;
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: boolean;
	[ELEMENTS.ROWS.REDACTIONS]: boolean;
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: boolean;
} = {
	[ELEMENTS.ROWS.CUSTOM_CONTENT]: false,
	[ELEMENTS.ROWS.STATUS]: false,
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: false,
	[ELEMENTS.ROWS.REDACTIONS]: false,
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: false,
};

export const DATABASE_ELEMENT_VISIBILITY: {
	[ELEMENTS.ROWS.VIEWS]: boolean;
	[ELEMENTS.ROWS.LENGTH]: boolean;
	[ELEMENTS.ROWS.STATUS]: boolean;
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: boolean;
	[ELEMENTS.ROWS.REDACTIONS]: boolean;
	[ELEMENTS.ROWS.LABELS]: boolean;
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: boolean;
	[ELEMENTS.SECTIONS.CONNECTIONS]: boolean;
} = {
	[ELEMENTS.ROWS.VIEWS]: false,
	[ELEMENTS.ROWS.LENGTH]: false,
	[ELEMENTS.ROWS.STATUS]: false,
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: false,
	[ELEMENTS.ROWS.REDACTIONS]: false,
	[ELEMENTS.ROWS.LABELS]: false, // Overridden below
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: false,
	[ELEMENTS.SECTIONS.CONNECTIONS]: false,
};

export const WHITEBOARDS_ELEMENT_VISIBILITY: {
	[ELEMENTS.ROWS.VIEWS]: boolean;
	[ELEMENTS.ROWS.LENGTH]: boolean;
	[ELEMENTS.ROWS.STATUS]: boolean;
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: boolean;
	[ELEMENTS.ROWS.REDACTIONS]: boolean;
	[ELEMENTS.ROWS.LABELS]: boolean;
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: boolean;
	[ELEMENTS.SECTIONS.CONNECTIONS]: boolean;
} = {
	[ELEMENTS.ROWS.VIEWS]: false,
	[ELEMENTS.ROWS.LENGTH]: false,
	[ELEMENTS.ROWS.STATUS]: false,
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: false,
	[ELEMENTS.ROWS.REDACTIONS]: false,
	[ELEMENTS.ROWS.LABELS]: false, // Overridden below
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: false,
	[ELEMENTS.SECTIONS.CONNECTIONS]: false,
};

export const SLIDES_ELEMENT_VISIBILITY: {
	[ELEMENTS.ROWS.VIEWS]: boolean;
	[ELEMENTS.ROWS.LENGTH]: boolean;
	[ELEMENTS.ROWS.STATUS]: boolean;
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: boolean;
	[ELEMENTS.ROWS.REDACTIONS]: boolean;
	[ELEMENTS.ROWS.LABELS]: boolean;
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: boolean;
	[ELEMENTS.SECTIONS.CONNECTIONS]: boolean;
} = {
	[ELEMENTS.ROWS.VIEWS]: false,
	[ELEMENTS.ROWS.LENGTH]: false,
	[ELEMENTS.ROWS.STATUS]: false,
	[ELEMENTS.ROWS.APPROVAL_ACTIVITY]: false,
	[ELEMENTS.ROWS.REDACTIONS]: false,
	[ELEMENTS.ROWS.LABELS]: false, // Overridden below
	[ELEMENTS.ROWS.PERFORMANCE_MODE]: false,
	[ELEMENTS.SECTIONS.CONNECTIONS]: false,
};

export const FOLDER_ELEMENT_VISIBILITY: {
	[ELEMENTS.ROWS.VIEWS]: boolean;
	[ELEMENTS.ROWS.LENGTH]: boolean;
	[ELEMENTS.ROWS.STATUS]: boolean;
	[ELEMENTS.SECTIONS.STATS]: boolean;
	[ELEMENTS.SECTIONS.CONNECTIONS]: boolean;
} = {
	[ELEMENTS.ROWS.VIEWS]: false,
	[ELEMENTS.ROWS.LENGTH]: false,
	[ELEMENTS.ROWS.STATUS]: false,
	[ELEMENTS.SECTIONS.STATS]: false,
	[ELEMENTS.SECTIONS.CONNECTIONS]: false,
};

type SpaceOverviewElementKey = VisibilityKeys<typeof SPACE_OVERVIEW_ELEMENT_VISIBILITY>;

type DatabaseElementKey = VisibilityKeys<typeof DATABASE_ELEMENT_VISIBILITY>;

type WhiteboardElementKey = VisibilityKeys<typeof WHITEBOARDS_ELEMENT_VISIBILITY>;

type SlideElementKey = VisibilityKeys<typeof SLIDES_ELEMENT_VISIBILITY>;

type FolderElementKey = VisibilityKeys<typeof FOLDER_ELEMENT_VISIBILITY>;

type ElementKey =
	| SpaceOverviewElementKey
	| DatabaseElementKey
	| WhiteboardElementKey
	| SlideElementKey
	| FolderElementKey;

export const useElementVisibility = (): {
	isElementVisible: (element: ElementKey) => boolean;
} => {
	const isSpaceOverviewViewMode = isSpaceOverview();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const [contentType] = useContentType();
	const homepageId = useSpaceHomepageId(spaceKey);
	const isSpaceOverviewEditMode = homepageId === contentId;
	const isSpaceOverviewPage = isSpaceOverviewViewMode || isSpaceOverviewEditMode;

	const getVisibilityConfig = () => {
		if (isSpaceOverviewPage) return SPACE_OVERVIEW_ELEMENT_VISIBILITY;
		if (contentType === ContentType.DATABASE) return DATABASE_ELEMENT_VISIBILITY;
		if (contentType === ContentType.WHITEBOARD) return WHITEBOARDS_ELEMENT_VISIBILITY;
		if (contentType === ContentType.SLIDE) return SLIDES_ELEMENT_VISIBILITY;
		if (contentType === ContentType.FOLDER) return FOLDER_ELEMENT_VISIBILITY;
		return null;
	};

	const isElementVisible = (element: ElementKey): boolean => {
		if (
			element === ELEMENTS.ROWS.LABELS &&
			(contentType === ContentType.DATABASE ||
				contentType === ContentType.WHITEBOARD ||
				contentType === ContentType.SLIDE)
		) {
			return true;
		}

		if (
			element === ELEMENTS.ROWS.VIEWS &&
			(contentType === ContentType.DATABASE || contentType === ContentType.WHITEBOARD)
		) {
			return expValEquals('whiteboard_database_analytics_exp', 'isEnabled', true);
		}

		const visibilityConfig = getVisibilityConfig();

		return (visibilityConfig && visibilityConfig[element]) ?? true;
	};

	return {
		isElementVisible,
	};
};
